import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import {
  IBase,
  IListExportPage,
  IPlaybackRange,
  ITrip,
  ITripDetail,
  ITripMinutePosition,
  ITripPosition,
  ITripStop,
  ITripWithPositions,
  IVehiclePositionMarker,
} from 'src/app/models';
import moment from 'moment';
import { APP } from 'src/app/config/app';
import { ITripsFilters } from 'src/app/filters';
import { IBookingDetails } from 'src/app/models/booking/bookingDetails';
import { ExpeditionStatus, ExpeditionType, ServiceType } from 'src/app/models/enums';
import { objectToCamel } from 'ts-case-convert';
import { BookingsHttpService } from '../bookings/bookings.http.service';
import { TripsHttpService } from './trips.http.service';
import { ITripDrivenRoute } from 'src/app/models/trip/tripDrivenRoute';

@Injectable({
  providedIn: 'root',
})
export class TripsService {
  constructor(
    private tripsHttpService: TripsHttpService,
    private bookingsHttpService: BookingsHttpService
  ) {}

  async getTrips(
    size: number,
    page: number,
    towns: IBase[]
  ) {
    const source$ = this.tripsHttpService.getTrips(
      size,
      page,
      towns
    );
    const response = await lastValueFrom(source$);
    const tripsArray = this.getData(response);
    return tripsArray;
  }

  async getTripsFiltered(
    size: number,
    page: number,
    expeditionClients: IBase[],
    expeditionOperators: IBase[],
    expeditionTowns: IBase[],
    expeditionTypes: ExpeditionType[],
    expeditionStatus: ExpeditionStatus[],
    searchValue?: string,
    expeditionServices?: IBase[],
    expeditionRoutes?: IBase[],
    dateOnly?: boolean,
    from?: Date,
    to?: Date,
    availabilityId?: number | null,
    expeditionDrivers?: IBase[],
    expeditionVehicles?: IBase[],
    expeditionServiceTypes?: ServiceType[]
  ) {
    const source$ = this.tripsHttpService.getTripsFiltered(
      size,
      page,
      expeditionClients,
      expeditionOperators,
      expeditionTowns,
      expeditionTypes,
      expeditionStatus,
      searchValue,
      expeditionServices,
      expeditionRoutes,
      dateOnly,
      from,
      to,
      availabilityId,
      expeditionDrivers,
      expeditionVehicles,
      expeditionServiceTypes
    );
    const response = await lastValueFrom(source$);
    const tripsArray = this.getData(response);
    return tripsArray;
  }

  async getTripsPositions(size: number, page: number, filters: ITripsFilters, searchValue?: string) {
    const source$ = this.tripsHttpService.getTripsPositions(size, page, filters, searchValue);
    const response = await lastValueFrom(source$);
    const data = this.getData(response);
    return { ...data, content: data.content.map((trip: ITripWithPositions) => this.withTripDelayConvention(trip)) };
  }

  private withTripDelayConvention(trip: ITripWithPositions): ITripWithPositions {
    const positions = (trip.positions ?? []).map((position) => ({
      ...position,
      delay: position.delay == null ? position.delay : -position.delay,
    }));
    return { ...trip, positions };
  }

  vehiclePositionsAt(
    trips: ITripWithPositions[],
    day: Date,
    minute: number,
    focusedTripId: number | null,
  ): IVehiclePositionMarker[] {
    const vehicles: IVehiclePositionMarker[] = [];
    for (const trip of trips) {
      if (focusedTripId !== null && trip.id !== focusedTripId) continue;
      if (!moment(trip.dateTime).isSame(day, 'day')) continue;
      const position = this.positionAt(trip.positions ?? [], minute);
      if (!position) continue;
      vehicles.push({
        tripId: trip.id!,
        color: trip.service?.color,
        latitude: position.latitude,
        longitude: position.longitude,
        delay: position.delay,
      });
    }
    return vehicles;
  }

  tripPlaybackRange(trip: ITripDetail): IPlaybackRange {
    const tripStart = moment(trip.dateTime);
    const startMinute = tripStart.hours() * APP.MINUTES_PER_HOUR + tripStart.minutes();
    const lastStop = trip.stops?.[trip.stops.length - 1];
    const lastStopTime = lastStop?.realTime || lastStop?.estimatedTime;
    const lastStopMinute = lastStopTime ? this.timeToMinutes(lastStopTime) : startMinute;
    const endMinute = lastStopMinute > startMinute ? lastStopMinute : APP.MINUTES_PER_DAY;
    return { startMinute, endMinute };
  }

  private timeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * APP.MINUTES_PER_HOUR + minutes;
  }

  private positionAt(positions: ITripMinutePosition[], minute: number): ITripMinutePosition | undefined {
    let low = 0;
    let high = positions.length - 1;
    let found: ITripMinutePosition | undefined;
    while (low <= high) {
      const middle = Math.floor((low + high) / 2);
      if (positions[middle].minute <= minute) {
        found = positions[middle];
        low = middle + 1;
      } else {
        high = middle - 1;
      }
    }
    if (!found || minute - found.minute > APP.POSITIONS_PLAYER_MAX_GAP_MINUTES) return undefined;
    return found;
  }

  async exportTrips(filters: ITripsFilters, searchValue?: string, visiblePage?: IListExportPage) {
    return lastValueFrom(this.tripsHttpService.exportTrips(filters, searchValue, visiblePage));
  }

  async getTrip(tripId: number) {
    const source$ = this.tripsHttpService.getTrip(tripId);
    const response = await lastValueFrom(source$);
    const trip = response as ITripDetail;
    trip.stops = objectToCamel(trip.stops) as Array<ITripStop>;
    return objectToCamel(trip);
  }

  async getBookingsByTrip(tripId: number) {
    const source$ = this.tripsHttpService.getBookingsByTrip(tripId);
    const response = await lastValueFrom(source$);
    const bookings = response as IBookingDetails[];
    return bookings;
  }

  async getBookingsByStop(tripId: number, stopId: number, pickUps: boolean) {
    const source$ = this.tripsHttpService.getBookingsByStop(tripId, stopId, pickUps);
    const response = await lastValueFrom(source$);
    const bookings = response as IBookingDetails[];
    return bookings;
  }

  async getChangeoversByStop(stopId: number) {
    const source$ = this.tripsHttpService.getChangeoversByStop(stopId);
    const response = await lastValueFrom(source$);
    const bookings = response as IBookingDetails[];
    return bookings;
  }

  async getTripPosition(tripId: number) {
    const source$ = this.tripsHttpService.getTripPosition(tripId);
    const response = await lastValueFrom(source$);
    const position = response as ITripPosition;
    return objectToCamel(position);
  }

  async getTownsPositions(townsIds: number[]) {
    const source$ = this.tripsHttpService.getTownsPositions(townsIds);
    const response = await lastValueFrom(source$);
    const positions = response as ITripPosition[];
    return objectToCamel(positions);
  }

  async changeDriver(tripId: number, driverId: number) {
    const source$ = this.tripsHttpService.changeDriver(tripId, driverId);
    const response = await lastValueFrom(source$);
    const trip = response as ITripDetail;
    return objectToCamel(trip);
  }

  async changeVehicle(tripId: number, vehicleId: number) {
    const source$ = this.tripsHttpService.changeVehicle(tripId, vehicleId);
    const response = await lastValueFrom(source$);
    const trip = response as ITripDetail;
    return objectToCamel(trip);
  }

  async addPax(tripId: number, nPax: number) {
    const source$ = this.tripsHttpService.addPax(tripId, nPax);
    const response = await lastValueFrom(source$);
    const tripDetails = response as ITripDetail;
    return objectToCamel(tripDetails);
  }

  async updateComment(tripId: number, comment: string | null) {
    const source$ = this.tripsHttpService.updateComment(tripId, comment);
    const response = await lastValueFrom(source$);
    const trip = response as ITripDetail;
    return objectToCamel(trip);
  }

  async setStatus(tripId: number, status: ExpeditionStatus) {
    const source$ = this.tripsHttpService.setStatus(tripId, status);
    const response = await lastValueFrom(source$);
    const trip = response as ITripDetail;
    return objectToCamel(trip);
  }

  async deleteTrip(tripId: number, data?: any) {
    const source$ = this.tripsHttpService.deleteTrip(tripId, data);
    const response = await lastValueFrom(source$);
    return response;
  }

  async validatePickUp(bookingId: number) {
    const source$ = this.bookingsHttpService.validatePickUp(bookingId);
    const response = await lastValueFrom(source$);
    const bookingDetails = response as IBookingDetails;
    return objectToCamel(bookingDetails);
  }

  async validateDropOff(bookingId: number) {
    const source$ = this.bookingsHttpService.validateDropOff(bookingId);
    const response = await lastValueFrom(source$);
    const bookingDetails = response as IBookingDetails;
    return objectToCamel(bookingDetails);
  }

  async getDrivenRoute(tripId: number) {
    const source$ = this.tripsHttpService.getDrivenRoute(tripId);
    const response = await lastValueFrom(source$);
    const positions = response as ITripDrivenRoute[];
    return objectToCamel(positions);
  }

  getData(response: any) {
    return response.data;
  }
}
